import React from 'react';
import { Menu, MenuItem, Divider, Box, Typography } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BRAND } from '../../theme/tokens';
import { Invoice } from '../../data/types';

const fmt = (n: number) =>
  '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  salesMenuAnchor: HTMLElement | null;
  onSalesMenuAnchor: (el: HTMLElement | null) => void;
  onCreateInvoice: () => void;
  onNavigateInvoice: (id: string) => void;
  onAllInvoices: () => void;
  jobInvoices: Invoice[];
  offendingCount: number;
  plCharge: number;
  blocked: boolean;
}

/** Floating footer — prototype .actions bar; + Sales Document hooks InvoicePage menu styling */
export const TmActionsBar: React.FC<Props> = ({
  salesMenuAnchor,
  onSalesMenuAnchor,
  onCreateInvoice,
  onNavigateInvoice,
  onAllInvoices,
  jobInvoices,
  offendingCount,
  plCharge,
  blocked,
}) => (
  <>
    <div className="tm-actions">
      <div className="inner">
        <div className="left">
          <button type="button" className="tm-btn orange split">
            <span className="lab">Save &amp; close</span>
            <span className="caret">▾</span>
          </button>
          <button type="button" className="tm-btn purple">Reschedule</button>
          <button
            type="button"
            className={`tm-btn purple split${blocked ? ' blocked' : ''}`}
            onClick={e => !blocked && onSalesMenuAnchor(e.currentTarget)}
          >
            <span className="lab">+ Sales Document</span>
            <span className="caret" onClick={e => { e.stopPropagation(); onSalesMenuAnchor(e.currentTarget.parentElement as HTMLElement); }}>▾</span>
          </button>
        </div>
        <div className="right">
          <button type="button" className="tm-btn ghost">Cancel</button>
          <button type="button" className="tm-btn purple">Delete job</button>
        </div>
      </div>
    </div>

    <Menu
      anchorEl={salesMenuAnchor}
      open={!!salesMenuAnchor}
      onClose={() => onSalesMenuAnchor(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ sx: { minWidth: 280, fontFamily: 'Nunito, system-ui, sans-serif' } }}
    >
      <MenuItem
        onClick={() => { onSalesMenuAnchor(null); if (!blocked) onCreateInvoice(); }}
        disabled={blocked}
        sx={{ py: 1 }}
      >
        <ReceiptIcon sx={{ fontSize: 16, color: BRAND, mr: 1.2 }} />
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>Invoice</Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#999' }}>
            {blocked
              ? `Blocked — ${offendingCount} PO${offendingCount > 1 ? 's' : ''} not reconciled`
              : `Create from billable lines · ${fmt(plCharge)} ex GST`}
          </Typography>
        </Box>
      </MenuItem>
      {jobInvoices.length > 0 && [
        <Divider key="div" />,
        ...jobInvoices.map(inv => (
          <MenuItem key={inv.id} onClick={() => { onSalesMenuAnchor(null); onNavigateInvoice(inv.id); }} sx={{ py: 0.7 }}>
            <OpenInNewIcon sx={{ fontSize: 13, color: '#bbb', mr: 1.2 }} />
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: BRAND, mr: 1 }}>{inv.id}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>{inv.status} · {fmt(inv.exGst)} ex GST</Typography>
          </MenuItem>
        )),
      ]}
      <Divider />
      <MenuItem onClick={() => { onSalesMenuAnchor(null); onAllInvoices(); }} sx={{ py: 0.8 }}>
        <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#555' }}>All invoices →</Typography>
      </MenuItem>
    </Menu>
  </>
);
